import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { LoggerService } from '@core/log/services/logger';
import { getSettingsAsync, silentCheckSsoRedirectUri } from '@core/sso/keycloak-server-config';

/**
 * La parte di `Keycloak` che serve al rientro: un'istanza a perdere che interroga il server e consegna il
 * refresh token ottenuto.
 */
export type SsoProbe = Pick<Keycloak, 'init' | 'refreshToken'>;

/**
 * Tempo oltre il quale l'interrogazione è data per persa. L'iframe nascosto annuncia l'esito con un messaggio che
 * può non arrivare mai: senza questo limite la scheda del browser non arriverebbe né al rientro né al messaggio
 * di sessione scaduta.
 */
export const SILENT_SSO_RECOVERY_TIMEOUT_MS = 15_000;

export interface SilentSsoRecoveryOptions {
  /** Fabbrica dell'istanza che interroga il server: le prove la sostituiscono con un doppio. */
  createProbe?: () => Promise<SsoProbe>;
}

/** Titoli con cui `keycloak-js` contrassegna gli iframe nascosti del controllo silenzioso. */
const HIDDEN_IFRAME_SELECTOR = 'iframe[title="keycloak-silent-check-sso"], iframe[title="keycloak-3p-check-iframe"]';

/**
 * Toglie dal documento gli iframe nascosti dell'interrogazione abbandonata.
 *
 * `keycloak-js` li rimuove soltanto dentro il gestore del messaggio che ne annuncia l'esito: se quel messaggio
 * non arriva mai, restano nel documento per tutta la vita della pagina. Una scheda del browser ritenta il
 * rientro a ogni ritorno in primo piano, quindi senza questa pulizia gli iframe si accumulano.
 */
const removeAbandonedIframes = (): void => {
  document.querySelectorAll(HIDDEN_IFRAME_SELECTOR).forEach((iframe) => {
    iframe.remove();
  });
};

/**
 * Rinuncia al tentativo quando il tempo concesso è trascorso, invece di attenderlo per sempre.
 *
 * Nessuna API permette di annullare l'interrogazione già partita, quindi rinunciare significa dichiararla
 * abbandonata con `giveUp`: continua per conto suo, ma il suo esito non tocca più nulla.
 */
const givingUpAfter = async (attempt: Promise<boolean>, timeoutMs: number, giveUp: () => void): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const givenUp = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      giveUp();
      resolve(false);
    }, timeoutMs);
  });

  try {
    return await Promise.race([attempt, givenUp]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Interroga il server in un iframe nascosto, senza mai lasciare la pagina.
 *
 * Un'istanza già avviata non può ripetere `init`, e il controllo silenzioso parte solo da lì: serve quindi
 * un'istanza a perdere. L'indirizzo di ritorno resta quello del controllo silenzioso, che si limita ad
 * annunciare l'esito alla finestra padre: la scheda non viene né portata altrove né ricaricata.
 */
const probeSilently = async (createProbe: () => Promise<SsoProbe>): Promise<SsoProbe> => {
  const probe = await createProbe();
  await probe.init({
    pkceMethod: 'S256',
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: silentCheckSsoRedirectUri(),
    silentCheckSsoFallback: false,
    checkLoginIframe: false
  });
  return probe;
};

/** Costruisce l'istanza a perdere sulla stessa configurazione di server che usa la scheda. */
const createDefaultProbe = async (): Promise<SsoProbe> => {
  return new Keycloak(await getSettingsAsync());
};

/** Interrogazione in corso e modo per dichiararla abbandonata quando nessuno ne aspetta più l'esito. */
interface RunningAttempt {
  result: Promise<boolean>;
  giveUp: () => void;
}

/*
  Ritorno in primo piano, rifiuto del rinnovo e annuncio di un'altra scheda possono chiedere il rientro insieme, e
  due risposte adotterebbero due refresh token, di cui il token endpoint rifiuterebbe quello rimasto indietro. La
  chiave è l'istanza di `Keycloak`, che è una per scheda del browser.
*/
const attempts = new WeakMap<Keycloak, RunningAttempt>();

/**
 * Tenta il rientro nella sessione senza far perdere all'utente ciò che ha a video.
 *
 * Quando il token endpoint rifiuta il refresh token di questa scheda, la sessione al server può essere ancora
 * viva, perché a tenerla in vita è un'altra scheda del browser. L'interrogazione chiede al server un token nuovo
 * per la stessa sessione; il refresh token che ne esce viene passato alla scheda, che lo scambia subito. Lo
 * scambio serve perché solo la risposta del token endpoint riempie ciò che la scheda legge dopo, dalla scadenza
 * ai ruoli.
 *
 * @returns Funzione che tenta il rientro e dice se è riuscito.
 */
export const injectSilentSsoRecovery = (options?: SilentSsoRecoveryOptions): (() => Promise<boolean>) => {
  const keycloak = inject(Keycloak);
  const logger = inject(LoggerService);
  const createProbe = options?.createProbe ?? createDefaultProbe;

  /**
   * Interroga il server e, se la sessione è ancora viva, fa scambiare alla scheda il refresh token ottenuto.
   *
   * Un esito che arriva quando `isAbandoned` è ormai vero non tocca più il token della scheda: il messaggio di
   * sessione scaduta è già a video, e riautenticare di nascosto lo lascerebbe davanti a una sessione viva.
   */
  const tryRecovery = async (isAbandoned: () => boolean): Promise<boolean> => {
    try {
      const probe = await probeSilently(createProbe);
      const recoveredRefreshToken = probe.refreshToken;
      if (!recoveredRefreshToken || isAbandoned()) {
        return false;
      }

      keycloak.refreshToken = recoveredRefreshToken;
      await keycloak.updateToken(-1);
      return true;
    } catch (error) {
      /*
        Quando il rientro non riesce, il chiamante mostra all'utente il messaggio di sessione scaduta: un
        errore lasciato uscire da qui salterebbe quel messaggio.
      */
      logger.warn(`Rientro silenzioso nella sessione non riuscito: ${String(error)}`);
      return false;
    }
  };

  /**
   * Avvia l'interrogazione e la registra finché non si conclude.
   *
   * Gli iframe si rimuovono alla conclusione e non allo scadere dell'attesa: prima l'interrogazione è ancora in
   * corso e, senza il proprio iframe, non riceverebbe mai l'esito.
   */
  const startAttempt = (): RunningAttempt => {
    let givenUp = false;
    const attempt: RunningAttempt = {
      result: tryRecovery(() => givenUp),
      giveUp: (): void => {
        givenUp = true;
      }
    };
    attempts.set(keycloak, attempt);
    void attempt.result.finally(() => {
      if (attempts.get(keycloak) === attempt) {
        attempts.delete(keycloak);
      }
      removeAbandonedIframes();
    });
    return attempt;
  };

  return async (): Promise<boolean> => {
    const attempt = attempts.get(keycloak) ?? startAttempt();
    return await givingUpAfter(attempt.result, SILENT_SSO_RECOVERY_TIMEOUT_MS, attempt.giveUp);
  };
};
